'use client';

import PlacesAutocomplete from '@c/forms/controls/PlacesAutocomplete';
import PageSection from '@c/listing/shared/PageSection';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import { LocateFixed, MapPin, SearchIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCreateListing } from '../_context';

type LatLng = { lat: number; lng: number };

const MAPS_LIBRARIES = ['places'] as 'places'[];
// Geographic center of the contiguous US — fallback when no location is set.
const DEFAULT_CENTER: LatLng = { lat: 39.8283, lng: -98.5795 };
const PLACE_ZOOM = 11;
const COUNTRY_ZOOM = 4;

export default function BikeLocation() {
  // The Google Maps SDK is heavy, so it loads only once the seller engages the
  // location field — focusing the input mounts <BikeLocationMap />, which is
  // what triggers useLoadScript.
  const [mapsRequested, setMapsRequested] = useState(false);

  return (
    <PageSection title="Vehicle Location" required>
      <div className="mt-8 flex w-full flex-col gap-6">
        {mapsRequested ? (
          <BikeLocationMap />
        ) : (
          <div className="flex w-full flex-col gap-6">
            <div className="flex h-[5.5rem] w-full max-w-[70rem] items-center justify-between gap-[0.8rem] rounded-[2rem] border border-[#D9D9D9] bg-white px-[1.6rem]">
              <input
                type="search"
                placeholder="Search by city"
                onFocus={() => setMapsRequested(true)}
                className="h-full w-full border-none bg-transparent text-[1.8rem] outline-none"
              />
              <SearchIcon className="h-6 w-6 flex-shrink-0 text-zinc-600" />
            </div>
            <div className="flex h-[32rem] w-full max-w-[70rem] flex-col items-center justify-center gap-[1.6rem] rounded-[1.2rem] border border-dashed border-[#D9D9D9] bg-brand-lightest-gray">
              <div className="flex h-[6.4rem] w-[6.4rem] items-center justify-center rounded-full bg-white shadow-sm">
                <MapPin className="h-[3.2rem] w-[3.2rem] text-zinc-400" />
              </div>
              <div className="flex flex-col items-center gap-[0.4rem]">
                <p className="text-[1.8rem] font-semibold text-zinc-600">
                  Map preview
                </p>
                <p className="text-[1.5rem] text-zinc-500">
                  Search a city above to see it pinned on the map
                </p>
              </div>
            </div>
          </div>
        )}

        <p className="text-[1.5rem] text-zinc-600">
          As privacy and safety is one of our top priorities, we only use your
          location to give buyers an approximate idea of where your bike is
          located. Exact location is never shown on MX Locker.
        </p>
      </div>
    </PageSection>
  );
}

// Search input + interactive map. Mounted lazily by <BikeLocation /> so the
// Google Maps SDK is fetched only when the seller actually needs it.
function BikeLocationMap() {
  const { form, product } = useCreateListing();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries: MAPS_LIBRARIES,
  });

  const initialMarker: LatLng | null = product.location
    ? { lat: product.location.latitude, lng: product.location.longitude }
    : null;

  const [marker, setMarker] = useState<LatLng | null>(initialMarker);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [locating, setLocating] = useState(false);
  // Initial camera, frozen at mount — passing live center/zoom props to
  // <GoogleMap> would override the imperative panTo()/setZoom() calls below.
  const [initialCenter] = useState<LatLng>(initialMarker ?? DEFAULT_CENTER);
  const [initialZoom] = useState(initialMarker ? PLACE_ZOOM : COUNTRY_ZOOM);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // Focus the search input once the SDK is ready — the seller focused the
  // field to get here, so move the cursor back to where they expect it.
  useEffect(() => {
    if (isLoaded) autocompleteRef.current?.querySelector('input')?.focus();
  }, [isLoaded]);

  // Recenter the map on the pin whenever it moves.
  useEffect(() => {
    if (!map || !marker) return;
    map.panTo(marker);
    map.setZoom(PLACE_ZOOM);
  }, [map, marker]);

  // `location` is rounded to ~1km so the exact address is never exposed.
  const applyLocation = (coords: LatLng, address: string) => {
    setMarker(coords);
    form.setValue('location', {
      longitude: +coords.lng.toFixed(2),
      latitude: +coords.lat.toFixed(2),
    });
    form.setValue('location_display', address);
  };

  const handleSelect = ({
    lat,
    lng,
    address,
  }: {
    lat?: number;
    lng?: number;
    address: string;
  }) => {
    if (lat != null && lng != null) {
      applyLocation({ lat, lng }, address);
    } else {
      form.setValue('location_display', address);
    }
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        let address = product.location_display ?? '';
        try {
          const res = await fetch(
            `/api/geocode?lat=${coords.lat}&lng=${coords.lng}`
          );
          const data = await res.json();
          if (data?.address) address = data.address;
        } catch {
          // reverse geocode is best-effort — keep the pin regardless
        }
        applyLocation(coords, address);
        setLocating(false);
      },
      () => setLocating(false)
    );
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {isLoaded ? (
        <div ref={autocompleteRef}>
          <PlacesAutocomplete
            requireLatLng
            MXAuto={false}
            setSelected={handleSelect}
            className="!h-[5.5rem] !rounded-[2rem]"
          />
        </div>
      ) : (
        <div className="h-[5.5rem] w-full max-w-[70rem] animate-pulse rounded-[2rem] bg-brand-lightest-gray" />
      )}

      <div className="relative h-[32rem] w-full max-w-[70rem] overflow-hidden rounded-[1.2rem] bg-brand-lightest-gray">
        {isLoaded && (
          <GoogleMap
            mapContainerClassName="h-full w-full"
            center={initialCenter}
            zoom={initialZoom}
            onLoad={setMap}
            options={{
              disableDefaultUI: true,
              clickableIcons: false,
              gestureHandling: 'greedy',
            }}
          >
            {marker && <MarkerF position={marker} />}
          </GoogleMap>
        )}
        <button
          type="button"
          onClick={handleLocateMe}
          disabled={locating || !isLoaded}
          aria-label="Use my current location"
          className="absolute right-[1.6rem] top-[1.6rem] flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-full bg-white shadow-md transition-colors hover:bg-zinc-50 disabled:opacity-60"
        >
          <LocateFixed className="h-[2rem] w-[2rem] text-black" />
        </button>
      </div>
    </div>
  );
}
